import { useSession } from '@side/next-auth/react';
import { format, subDays } from 'date-fns';
import { useUserPermissionStates } from '@/common/hooks/useUserPermissionStates';
import { useCore } from '$api-clients/hooks';

/**
 * Returns a facet filters to exclude the user's id from a search
 * @param ids user ids to exclude
 * @returns boolean
 */
function buildUserFacet(ids: (string | null | undefined)[]) {
  return ids
    .filter((id): id is string => !!id)
    .map((id) => `-owner:${id}`)
    .join(',');
}

export const useSideListings = () => {
  const user = useSession()?.data?.user;
  const { submitAsStates, loading: statesLoading } = useUserPermissionStates({
    userId: user?.id,
  });

  const userIdFacet = buildUserFacet([user?.id, user?.legacyExternalId]);
  const stateFacets = submitAsStates.map((state) => `state:${state}`).join(',');

  const currentDate = new Date();
  const yesterday = format(subDays(currentDate, 1), 'yyyy-MM-dd');

  const { data: allListings, isLoading: allListingsLoading } = useCore(
    '/community/listings',
    submitAsStates.length
      ? {
          params: {
            query: {
              limit: 4,
              facets: `${userIdFacet},${stateFacets}`,
            },
          },
        }
      : null,
  );
  const { data: newListings, isLoading: newListingsLoading } = useCore(
    '/community/listings',
    submitAsStates.length
      ? {
          params: {
            query: {
              limit: 1,
              facets: `${userIdFacet},${stateFacets},dateAdded:[${yesterday} TO *]`,
            },
          },
        }
      : null,
  );

  return {
    featuredListings: allListings?.results ?? [],
    totalListings: allListings?.count ?? 0,
    offMarketListings: allListings?.facets?.status.offMarket ?? 0,
    newOffMarketListings: newListings?.facets?.status.offMarket ?? 0,
    comingSoonListings: allListings?.facets?.status.comingSoon ?? 0,
    newComingSoonListings: newListings?.facets?.status.comingSoon ?? 0,
    loading: statesLoading || allListingsLoading || newListingsLoading,
  };
};
